import React, { useEffect, useRef, useState } from 'react'
import { PrismicLinkType } from 'src/typings/generated/graphql'
import { customLinkResolver } from 'src/utils/linkResolver'
import * as Styles from './optionSelectModal.module.scss'

export type OptionSelectModalContent = {
  title: string
  option1Label: string
  option1Link?: PrismicLinkType
  option2Label: string
  option2Link?: PrismicLinkType
  continueText: string
}

type Props = {
  content: OptionSelectModalContent
  isOpen: boolean
  onClose: () => void
}

type Selection = 'option1' | 'option2' | null

const OptionSelectModal = ({ content, isOpen, onClose }: Props) => {
  const {
    title,
    option1Label,
    option1Link,
    option2Label,
    option2Link,
    continueText,
  } = content
  const [selection, setSelection] = useState<Selection>(null)
  const dialogRef = useRef<HTMLDivElement>(null)

  useEffect(() => {
    if (!isOpen) return undefined

    setSelection(null)

    const handleKey = (event: KeyboardEvent) => {
      if (event.key === 'Escape') onClose()
    }
    document.addEventListener('keydown', handleKey)

    const previousOverflow = document.body.style.overflow
    document.body.style.overflow = 'hidden'

    dialogRef.current?.focus()

    return () => {
      document.removeEventListener('keydown', handleKey)
      document.body.style.overflow = previousOverflow
    }
  }, [isOpen, onClose])

  if (!isOpen) return null

  const handleContinue = () => {
    const link = selection === 'option1' ? option1Link : option2Link
    if (!link) {
      onClose()
      return
    }
    const resolved = customLinkResolver(link)
    if (!resolved.linkPath || resolved.linkPath === '/') {
      onClose()
      return
    }
    const target = link.target ?? '_self'
    if (typeof window !== 'undefined') {
      if (target === '_blank') {
        window.open(resolved.linkPath, '_blank', 'noopener,noreferrer')
      } else {
        window.location.href = resolved.linkPath
      }
    }
    onClose()
  }

  const handleOverlayClick = (event: React.MouseEvent<HTMLDivElement>) => {
    if (event.target === event.currentTarget) onClose()
  }

  return (
    <div
      className={Styles.overlay}
      role="presentation"
      onClick={handleOverlayClick}
    >
      <div
        ref={dialogRef}
        className={Styles.dialog}
        role="dialog"
        aria-modal="true"
        aria-labelledby="option-select-modal-title"
        tabIndex={-1}
      >
        <button
          type="button"
          className={Styles.closeButton}
          onClick={onClose}
          aria-label="Close dialog"
        >
          ✕
        </button>
        <h2 id="option-select-modal-title" className={Styles.title}>
          {title}
        </h2>
        <div className={Styles.options} role="radiogroup" aria-label={title}>
          <label
            className={Styles.option}
            htmlFor="option-select-modal-option1"
          >
            <input
              id="option-select-modal-option1"
              type="radio"
              name="option-select-modal"
              value="option1"
              checked={selection === 'option1'}
              onChange={() => setSelection('option1')}
            />
            <span>{option1Label}</span>
          </label>
          <label
            className={Styles.option}
            htmlFor="option-select-modal-option2"
          >
            <input
              id="option-select-modal-option2"
              type="radio"
              name="option-select-modal"
              value="option2"
              checked={selection === 'option2'}
              onChange={() => setSelection('option2')}
            />
            <span>{option2Label}</span>
          </label>
        </div>
        <button
          type="button"
          className={Styles.continueButton}
          onClick={handleContinue}
          disabled={selection === null}
        >
          {continueText}
        </button>
      </div>
    </div>
  )
}

export default OptionSelectModal
